import { useEffect, useState } from "react";

/**
 * Flat hero illustration: a developer sitting cross-legged with a laptop whose
 * screen faces the viewer and shows the current stage, wrapped by a CI/CD
 * pipeline of cards connected with arrows. Stage cycles boot → code → git push
 * → CI/CD → deploy ✓ (the laptop screen + the active card stay in sync).
 * Pure SVG + CSS keyframes; reduced-motion safe.
 */

const NODES = [
  { k: "boot", label: "Boot up", chip: "power on", color: "#22d3ee", x: 22, y: 312 },
  { k: "code", label: "Writing code", chip: "Portfolio.jsx", color: "#8b5cf6", x: 92, y: 166 },
  { k: "git", label: "git push", chip: "origin main", color: "#ec4899", x: 336, y: 96 },
  { k: "cicd", label: "CI / CD", chip: "build · test", color: "#fbbf24", x: 596, y: 150 },
  { k: "deploy", label: "Deployed", chip: "live ✓", color: "#34d399", x: 712, y: 312 },
];

const ARROWS = [
  "M104 312 Q70 270 140 232",
  "M242 184 Q300 168 334 156",
  "M486 120 Q566 130 600 166",
  "M700 202 Q752 250 758 308",
];

const GLYPHS = [
  { x: 60, y: 470, t: "</>", c: "#22d3ee", s: 26, d: 0 },
  { x: 470, y: 545, t: "( )", c: "#ec4899", s: 24, d: 1.4 },
  { x: 836, y: 470, t: "=>", c: "#34d399", s: 24, d: 0.7 },
];

const CODE = [
  [{ x: 384, w: 24, c: "#22d3ee" }, { x: 412, w: 44, c: "#9cdcfe" }],
  [{ x: 396, w: 34, c: "#c586c0" }, { x: 434, w: 28, c: "#ce9178" }],
  [{ x: 396, w: 60, c: "#dcdcaa" }],
  [{ x: 408, w: 26, c: "#569cd6" }, { x: 438, w: 38, c: "#9cdcfe" }],
  [{ x: 396, w: 20, c: "#c586c0" }, { x: 420, w: 32, c: "#9cdcfe" }],
];

function StageIcon({ k }) {
  const s = { stroke: "#fff", strokeWidth: 2.4, fill: "none", strokeLinecap: "round", strokeLinejoin: "round" };
  if (k === "boot") return (<g {...s}><path d="M0 -7 L0 1" /><path d="M-6 -3 A7 7 0 1 0 6 -3" /></g>);
  if (k === "code") return (<g {...s}><path d="M-3 -6 L-9 0 L-3 6" /><path d="M3 -6 L9 0 L3 6" /></g>);
  if (k === "git") return (<g {...s}><path d="M0 7 L0 -6" /><path d="M-5 -1 L0 -6 L5 -1" /></g>);
  if (k === "cicd") return (<g {...s}><path d="M7 0 A7 7 0 1 1 3.5 -6" /><path d="M3.5 -7.5 L3.5 -3 L8 -3" /></g>);
  return (<g {...s}><path d="M-6 0 L-2 5 L7 -7" /></g>);
}

function Node({ node, state }) {
  const isActive = state === "active";
  const isDone = state === "done";
  const accent = isActive || isDone ? node.color : "#3b3b4d";
  return (
    <g>
      <rect x={node.x} y={node.y} width="150" height="54" rx="14" fill={isActive ? "#1b1b2a" : "#14141d"} stroke={accent} strokeWidth={isActive ? 2 : 1.2} strokeOpacity={isActive ? 1 : isDone ? 0.7 : 0.45} style={isActive ? { filter: `drop-shadow(0 0 9px ${node.color})` } : undefined} />
      <rect x={node.x + 12} y={node.y + 12} width="30" height="30" rx="9" fill={node.color} opacity={isActive ? 1 : isDone ? 0.85 : 0.42} />
      <g transform={`translate(${node.x + 27} ${node.y + 27})`} opacity={isActive || isDone ? 1 : 0.6}>
        <StageIcon k={node.k} />
      </g>
      <text x={node.x + 50} y={node.y + 25} fill={isActive ? "#f8fafc" : isDone ? "#cbd5e1" : "#6b7280"} fontSize="14" fontWeight="700" fontFamily="'Inter',sans-serif">{node.label}</text>
      <text x={node.x + 50} y={node.y + 40} fill={isActive ? "#aab2c2" : "#596073"} fontSize="10.5" fontFamily="'Consolas',monospace">{node.chip}</text>
      {isActive && <circle cx={node.x + 137} cy={node.y + 13} r="4.5" fill={node.color} className="pulse" />}
      {isDone && (
        <g transform={`translate(${node.x + 137} ${node.y + 13})`}>
          <circle r="7" fill={node.color} />
          <path d="M-3 0 l2 2.4 l4.5 -5" stroke="#0a0a14" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </g>
      )}
    </g>
  );
}

const mono = "'Consolas','Menlo',monospace";

function ScreenContent({ k }) {
  if (k === "boot") {
    return (
      <g className="screen-swap">
        <circle cx="450" cy="392" r="12" fill="none" stroke="#ffffff22" strokeWidth="3" />
        <g className="spin"><circle cx="450" cy="392" r="12" fill="none" stroke="#22d3ee" strokeWidth="3" strokeDasharray="34 100" strokeLinecap="round" /></g>
        <text x="450" y="420" textAnchor="middle" fontSize="10" fill="#9aa4b2" fontFamily={mono}>booting…</text>
      </g>
    );
  }
  if (k === "code") {
    return (
      <g className="screen-swap">
        {CODE.map((segs, li) => segs.map((s, si) => (
          <rect key={`${li}-${s.x}`} x={s.x} y={364 + li * 13} width={s.w} height="5" rx="2.5" fill={s.c} className="type" style={{ animationDelay: `${0.12 + (li * 2 + si) * 0.16}s` }} />
        )))}
        <rect x="452" y="416" width="6" height="6" rx="1" fill="#a78bfa" className="caret" />
      </g>
    );
  }
  if (k === "git") {
    return (
      <g className="screen-swap" fontFamily={mono} fontSize="9.5">
        <text x="386" y="376" className="linein" style={{ animationDelay: "0.1s" }}><tspan fill="#22d3ee">$ </tspan><tspan fill="#e6e6e6">git push origin</tspan></text>
        <text x="386" y="392" className="linein" style={{ animationDelay: "0.5s" }} fill="#8b8b9e">Writing objects: 100%</text>
        <text x="386" y="408" className="linein" style={{ animationDelay: "1s" }} fill="#4ade80">✓ pushed to GitHub</text>
      </g>
    );
  }
  if (k === "cicd") {
    return (
      <g className="screen-swap" fontFamily={mono} fontSize="9.5">
        <text x="386" y="374" className="linein" style={{ animationDelay: "0.1s" }}><tspan fill="#a78bfa">▶ </tspan><tspan fill="#e6e6e6">pipeline</tspan></text>
        <text x="386" y="389" className="linein" style={{ animationDelay: "0.45s" }}><tspan fill="#4ade80">✓ </tspan><tspan fill="#cbd5e1">install · test</tspan></text>
        <text x="386" y="404" className="linein" style={{ animationDelay: "0.8s" }}><tspan fill="#4ade80">✓ </tspan><tspan fill="#cbd5e1">build bundle</tspan></text>
        <text x="386" y="419" className="linein" style={{ animationDelay: "1.2s" }}><tspan fill="#fbbf24" className="dotpulse">● </tspan><tspan fill="#cbd5e1">deploying…</tspan></text>
      </g>
    );
  }
  return (
    <g className="screen-swap">
      <g className="pop">
        <circle cx="450" cy="386" r="15" fill="none" stroke="#34d399" strokeWidth="3" />
        <path d="M442 386 l5 6 l9 -11" stroke="#34d399" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      </g>
      <text x="450" y="418" textAnchor="middle" fontSize="10" fill="#34d399" fontFamily={mono} className="linein" style={{ animationDelay: "0.35s" }}>deployed ✓</text>
    </g>
  );
}

export default function HeroCoder() {
  const [phase, setPhase] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setPhase((p) => (p + 1) % NODES.length), 2400);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-visible">
      <div className="absolute h-[34rem] w-[36rem] rounded-full bg-aurora-violet/22 blur-[120px]" />
      <div className="absolute h-[22rem] w-[28rem] rounded-full bg-aurora-cyan/12 blur-[110px]" style={{ transform: "translate(5rem, 5rem)" }} />
      <div className="absolute h-[20rem] w-[24rem] rounded-full bg-aurora-pink/10 blur-[110px]" style={{ transform: "translate(-6rem, 4rem)" }} />

      <svg viewBox="0 0 900 600" className="coder relative h-full w-full" aria-hidden="true">
        <defs>
          <linearGradient id="hoodie" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#8b5cf6" />
            <stop offset="1" stopColor="#5b54e0" />
          </linearGradient>
          <linearGradient id="pants" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#262640" />
            <stop offset="1" stopColor="#1a1a2c" />
          </linearGradient>
          <linearGradient id="screeng" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#0e1320" />
            <stop offset="1" stopColor="#0a0e18" />
          </linearGradient>
          <radialGradient id="scrglow" cx="0.5" cy="0.5" r="0.6">
            <stop offset="0" stopColor="#5b7bff" stopOpacity="0.4" />
            <stop offset="1" stopColor="#5b7bff" stopOpacity="0" />
          </radialGradient>
          <marker id="ah-on" markerWidth="7" markerHeight="7" refX="5" refY="3" orient="auto" markerUnits="strokeWidth">
            <path d="M0 0 L6 3 L0 6 Z" fill="#a78bfa" />
          </marker>
          <marker id="ah-off" markerWidth="7" markerHeight="7" refX="5" refY="3" orient="auto" markerUnits="strokeWidth">
            <path d="M0 0 L6 3 L0 6 Z" fill="#3b3b4d" />
          </marker>
        </defs>

        {GLYPHS.map((g) => (
          <text key={g.t + g.x} x={g.x} y={g.y} fill={g.c} fontSize={g.s} fontFamily={mono} fontWeight="600" className="glyph" style={{ animationDelay: `${g.d}s` }}>{g.t}</text>
        ))}

        {/* pipeline arrows */}
        {ARROWS.map((d, i) => {
          const on = phase > i;
          const flowing = phase === i;
          return (
            <path key={d} d={d} fill="none" stroke={on || flowing ? "#a78bfa" : "#3b3b4d"} strokeWidth="2.4" strokeLinecap="round" strokeDasharray={flowing ? "7 8" : "0"} strokeOpacity={on ? 0.9 : flowing ? 1 : 0.5} markerEnd={on || flowing ? "url(#ah-on)" : "url(#ah-off)"} className={flowing ? "flow" : ""} />
          );
        })}

        {/* developer (centered, facing us, screen-out laptop) */}
        <ellipse cx="450" cy="520" rx="158" ry="22" fill="#000000" opacity="0.3" />
        <g className="person">
          {/* crossed legs */}
          <ellipse cx="450" cy="498" rx="120" ry="32" fill="url(#pants)" />
          <path d="M350 498 Q366 458 426 474 L506 498 Q566 516 408 516 Q338 516 350 498 Z" fill="#2c2c46" />
          <path d="M550 498 Q534 458 474 474 L394 498 Q334 516 492 516 Q562 516 550 498 Z" fill="#222238" />
          <ellipse cx="350" cy="498" rx="20" ry="11" fill="#33334e" />
          <ellipse cx="550" cy="498" rx="20" ry="11" fill="#2a2a42" />

          {/* shoulders / hoodie (behind the screen) */}
          <path d="M344 452 Q336 366 450 354 Q564 366 556 452 Z" fill="url(#hoodie)" />
          {/* arms framing the laptop */}
          <path d="M356 378 Q324 420 350 462 L380 456 Q358 422 392 396 Z" fill="#7d6cf0" />
          <path d="M544 378 Q576 420 550 462 L520 456 Q542 422 508 396 Z" fill="#6a5ee6" />
          {/* hands on keyboard */}
          <ellipse cx="386" cy="460" rx="13" ry="9" fill="#d9a87e" />
          <ellipse cx="514" cy="460" rx="13" ry="9" fill="#d9a87e" />

          {/* neck + head (above the laptop) */}
          <rect x="434" y="318" width="32" height="26" rx="11" fill="#caa07a" />
          <ellipse cx="450" cy="294" rx="38" ry="40" fill="#d9a87e" />
          <path d="M410 296 Q404 244 450 240 Q496 244 490 298 Q484 262 450 258 Q416 262 410 296 Z" fill="#16161f" />
          <ellipse cx="450" cy="268" rx="40" ry="25" fill="#16161f" />
          <circle cx="437" cy="301" r="2.4" fill="#2a2233" />
          <circle cx="463" cy="301" r="2.4" fill="#2a2233" />
          {/* glasses */}
          <g stroke="#2b2b3d" strokeWidth="2.4">
            <rect x="425" y="291" width="23" height="18" rx="7" fill="#22d3ee" fillOpacity="0.07" />
            <rect x="452" y="291" width="23" height="18" rx="7" fill="#22d3ee" fillOpacity="0.07" />
          </g>
          <g stroke="#2b2b3d" strokeWidth="2.4" fill="none" strokeLinecap="round">
            <path d="M448 299 L452 299" />
            <path d="M425 296 L415 293" />
            <path d="M475 296 L485 293" />
          </g>

          {/* laptop (screen faces the viewer) */}
          <ellipse cx="450" cy="402" rx="130" ry="66" fill="url(#scrglow)" className="lapglow" />
          {/* base / keyboard */}
          <path d="M360 476 L540 476 L562 450 L338 450 Z" fill="#1a1a26" />
          <path d="M344 450 L556 450 L552 444 L348 444 Z" fill="#2a2a3c" />
          {/* screen */}
          <rect x="358" y="344" width="184" height="106" rx="12" fill="#1b1b28" />
          <rect x="370" y="354" width="160" height="86" rx="7" fill="url(#screeng)" />
          <ScreenContent key={NODES[phase].k} k={NODES[phase].k} />
          {/* camera dot */}
          <circle cx="450" cy="350" r="1.6" fill="#3a3a52" />
        </g>

        {/* pipeline nodes */}
        {NODES.map((node, i) => (
          <Node key={node.k} node={node} state={i === phase ? "active" : i < phase ? "done" : "pending"} />
        ))}
      </svg>

      <style>{`
        @keyframes coder-pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.4;transform:scale(0.7)} }
        @keyframes coder-lap { 0%,100%{opacity:0.5} 50%{opacity:0.85} }
        @keyframes coder-float { 0%,100%{transform:translateY(0);opacity:0.35} 50%{transform:translateY(-12px);opacity:0.6} }
        @keyframes coder-breathe { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-3px)} }
        @keyframes coder-flow { to { stroke-dashoffset: -30; } }
        @keyframes coder-spin { to { transform: rotate(360deg); } }
        @keyframes coder-swap { from { opacity: 0; } to { opacity: 1; } }
        @keyframes coder-type { from { transform: scaleX(0); } to { transform: scaleX(1); } }
        @keyframes coder-linein { from { opacity: 0; transform: translateX(-7px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes coder-pop { from { opacity: 0; transform: scale(0.4); } to { opacity: 1; transform: scale(1); } }
        @keyframes coder-blink { 0%,49%{opacity:1} 50%,100%{opacity:0} }
        .coder .pulse { transform-box: fill-box; transform-origin: center; animation: coder-pulse 1.4s ease-in-out infinite; }
        .coder .flow { animation: coder-flow 0.7s linear infinite; }
        .coder .lapglow { animation: coder-lap 3s ease-in-out infinite; }
        .coder .glyph { transform-box: fill-box; transform-origin: center; animation: coder-float 6s ease-in-out infinite; }
        .coder .person { transform-box: fill-box; transform-origin: center bottom; animation: coder-breathe 5s ease-in-out infinite; }
        .coder .spin { transform-box: fill-box; transform-origin: center; animation: coder-spin 1.1s linear infinite; }
        .coder .screen-swap { animation: coder-swap 0.4s ease both; }
        .coder .type { transform-box: fill-box; transform-origin: left center; animation: coder-type 0.4s ease both; }
        .coder .linein { transform-box: fill-box; animation: coder-linein 0.45s ease both; }
        .coder .pop { transform-box: fill-box; transform-origin: center; animation: coder-pop 0.5s cubic-bezier(0.22,1,0.36,1) both; }
        .coder .caret { animation: coder-blink 0.9s steps(1) infinite; }
        .coder .dotpulse { animation: coder-pulse 1.2s ease-in-out infinite; }
      `}</style>
    </div>
  );
}
